import {
  getRealtimeToken,
  getNoteRealtimeToken,
  getMimicRealtimeToken,
  getCommunityRealtimeToken,
  getBriefRealtimeToken,
  getBlackjackRealtimeToken,
  getHiloRealtimeToken,
  getQuiplipRealtimeToken,
  getPokerRealtimeToken,
  getDoodlrRealtimeToken,
  getBluffrRealtimeToken,
  getTable2RealtimeToken,
} from "../api/realtime-api";
import { getEnv } from "../utils/env";
import { DurableObjectTransport } from "./do-transport";
import type { RealtimeTransport } from "./types";

export type { ChannelStatus, FrameMeta, JoinOptions, RealtimeChannel, RealtimeTransport } from "./types";
export { CLOSE_SUPERSEDED, DurableObjectTransport, isPeerFrame, RT_SUBPROTOCOL } from "./do-transport";
export { HEARTBEAT_TIMEOUT_MS, ONLINE_PROBE_TIMEOUT_MS } from "./types";

export type RealtimeFeature =
  | "presence"
  | "notifications"
  | "communities"
  | "notes"
  | "games";

const FEATURE_FLAG: Record<RealtimeFeature, string> = {
  presence: "VITE_RT_PRESENCE",
  notifications: "VITE_RT_NOTIFICATIONS",
  communities: "VITE_RT_COMMUNITIES",
  notes: "VITE_RT_NOTES",
  games: "VITE_RT_GAMES",
};

/** Whether a feature should use the Cloudflare Durable Object transport (flag
 *  on) instead of the legacy Momento path. Reads the same VITE_RT_* flags the
 *  web app ships, via the platform adapter so Metro/mobile resolves them too. */
export function rtEnabled(feature: RealtimeFeature): boolean {
  return getEnv(FEATURE_FLAG[feature], "false") === "true";
}

let cached: DurableObjectTransport | null = null;

/** The ONE shared DO transport (lazily constructed) for web, desktop, and
 *  mobile — no app may construct its own. `join("<roomType>/<id>")` → e.g.
 *  "presence/app", "community/{id}:{channel}", "inbox/{userId}",
 *  "note/{id}", "board/{id}", "table2/{code}" (Card Table v2 → TableDO),
 *  "game/{prefix}-{roomId}".
 *  getToken routes each scope to its membership-checked backend endpoint. */
export function getRealtimeTransport(): RealtimeTransport {
  if (!cached) {
    const baseUrl = getEnv("VITE_RT_WS_URL", "wss://rt.slumbr.xyz");
    cached = new DurableObjectTransport({
      baseUrl,
      getToken: (scope) => routeRealtimeToken(scope),
    });
  }
  return cached;
}

/** Scope → token endpoint. Exported for tests. */
export function routeRealtimeToken(scope: string): Promise<string> {
  if (scope.startsWith("note:")) {
    return getNoteRealtimeToken(scope.slice("note:".length)).then((r) => r.token);
  }
  if (scope.startsWith("game:")) {
    const id = scope.slice("game:".length);
    // Blackjack rooms use a `bj-` prefixed id + their own token endpoint.
    if (id.startsWith("bj-")) {
      return getBlackjackRealtimeToken(id.slice("bj-".length)).then((r) => r.token);
    }
    // Higher-or-Lower rooms use a `hl-` prefixed id + their own token endpoint.
    if (id.startsWith("hl-")) {
      return getHiloRealtimeToken(id.slice("hl-".length)).then((r) => r.token);
    }
    // Quiplip rooms use a `ql-` prefixed id + their own token endpoint.
    if (id.startsWith("ql-")) {
      return getQuiplipRealtimeToken(id.slice("ql-".length)).then((r) => r.token);
    }
    // Poker (Texas Hold'em) rooms use a `pk-` prefixed id.
    if (id.startsWith("pk-")) {
      return getPokerRealtimeToken(id.slice("pk-".length)).then((r) => r.token);
    }
    // Doodlr rooms use a `dl-` prefixed id.
    if (id.startsWith("dl-")) {
      return getDoodlrRealtimeToken(id.slice("dl-".length)).then((r) => r.token);
    }
    // Bluffr rooms use a `bf-` prefixed id.
    if (id.startsWith("bf-")) {
      return getBluffrRealtimeToken(id.slice("bf-".length)).then((r) => r.token);
    }
    // Mimic rooms use an `mm-` prefixed id.
    if (id.startsWith("mm-")) {
      return getMimicRealtimeToken(id.slice("mm-".length)).then((r) => r.token);
    }
    // Every game room carries a prefix; a bare id has no endpoint that
    // could membership-check it.
    return Promise.reject(new Error(`No realtime token route for game scope "${scope}"`));
  }
  if (scope.startsWith("table2:")) {
    // Card Table v2: persona-keyed, optionally carrying a friends-only `friendOf` hint.
    return getTable2RealtimeToken(scope.slice("table2:".length)).then((r) => r.token);
  }
  if (scope.startsWith("board:")) {
    return getBriefRealtimeToken(scope.slice("board:".length)).then((r) => r.token);
  }
  if (scope.startsWith("community:")) {
    // scope = community:{communityId}:{channelId}
    const [communityId, channelId] = scope.slice("community:".length).split(":");
    return getCommunityRealtimeToken(communityId, channelId).then((r) => r.token);
  }
  return getRealtimeToken(scope).then((r) => r.token);
}

/** Test seam — drop the cached transport so a later getRealtimeTransport()
 *  rebuilds with fresh env/flags. */
export function resetRealtimeTransport(): void {
  cached = null;
}
